import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import axios from 'axios'
import { dequal as deepEqual } from 'dequal/lite'
import { get } from 'lodash'

const actions = {
  REQUEST_START: 'REQUEST_START',
  REQUEST_END: 'REQUEST_END',
  SET_LATEST_REQUEST_ID: 'SET_LATEST_REQUEST_ID',
}

const DEFAULT_OPTIONS = {
  manual: false,
}

const configToObject = (config) => {
  if (typeof config === 'string') {
    return {
      url: config,
    }
  }
  return Object.assign({}, config)
}

const isReactEvent = (obj) => {
  return obj && obj.nativeEvent && obj.nativeEvent instanceof Event
}

const useDeepCompareMemoize = (value) => {
  const ref = useRef()
  const signalRef = useRef(0)
  if (!deepEqual(value, ref.current)) {
    ref.current = value
    signalRef.current += 1
  }
  // Return a value reference that only changes when the deep-compared input changes.
  // signalRef is intentionally the memo trigger; this mirrors use-deep-compare-effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ref.current, [signalRef.current])
}

/**
 *
 * @param configAxios Config param is object config of axios library
 * @param options {manual: boolean}: default false - manual will execute immediately.
 *                {full: boolean}: defautl fasle - full will returns all response data instead of just data object.
 * If you want to call it manually, use manual: true
 *
 */
export const useHttps = (configAxios, optionsParam) => {
  const stableConfig = useDeepCompareMemoize(configAxios)
  const config = useMemo(() => configToObject(stableConfig), [stableConfig])

  const stableOptions = useDeepCompareMemoize(optionsParam)
  const options = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...stableOptions }),
    [stableOptions],
  )

  const cancelSourcesRef = useRef({})

  const latestRequestIdRef = useRef(null)

  const reducer = (state, action) => {
    switch (action.type) {
      case actions.REQUEST_START:
        return {
          ...state,
          loading: true,
          error: null,
        }
      case actions.REQUEST_END:
        // Only update if requestId is latest
        if (action.requestId && state.latestRequestId !== action.requestId) {
          return state
        }
        return {
          ...state,
          loading: false,
          ...(action.error ? { error: action.payload, data: null } : { data: action.payload, error: null }),
        }
      case actions.SET_LATEST_REQUEST_ID:
        return {
          ...state,
          latestRequestId: action.requestId,
        }
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    loading: false,
    error: null,
    data: null,
    latestRequestId: null,
  })

  const createCancelToken = () => {
    const requestId = Date.now() + '-' + Math.random().toString(36).substr(2, 5)
    const cancelSource = axios.CancelToken.source()
    cancelSource.requestId = requestId
    return cancelSource
  }

  const getCanonicalUrl = (url) => {
    try {
      const u = new URL(url, window.location.origin)
      return u.pathname
    } catch (err) {
      return url
    }
  }

  const cancelOutstandingRequest = useCallback((urlOrPath) => {
    const canonicalUrl = getCanonicalUrl(urlOrPath)
    if (cancelSourcesRef.current[canonicalUrl]) {
      cancelSourcesRef.current[canonicalUrl].cancel()
      delete cancelSourcesRef.current[canonicalUrl]
    }
  }, [])

  const withCancelToken = useCallback(
    (_config) => {
      const canonicalUrl = getCanonicalUrl(_config.url)
      cancelOutstandingRequest(canonicalUrl)
      const cancelSource = createCancelToken()
      cancelSourcesRef.current[canonicalUrl] = cancelSource
      _config.cancelToken = cancelSource.token
      _config.headers = {
        ..._config.headers,
        'X-Request-ID': cancelSource.requestId,
      }
      return { ..._config, __canonicalUrl: canonicalUrl, __requestId: cancelSource.requestId }
    },
    [cancelOutstandingRequest],
  )

  const executeRequest = async (config, dispatch, full, canonicalUrl, requestId) => {
    try {
      dispatch({ type: actions.REQUEST_START })
      dispatch({ type: actions.SET_LATEST_REQUEST_ID, requestId })
      latestRequestIdRef.current = requestId

      const { data } = await axios({ ...config, headers: { ...config.headers, 'X-Request-ID': requestId } })
      const configData = config.data || {}

      if (full) {
        dispatch({ type: actions.REQUEST_END, payload: data ? data || { status: 'success' } : configData, requestId })
      } else {
        dispatch({ type: actions.REQUEST_END, payload: data ? data.data || { status: 'success' } : configData, requestId })
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        let errMsg = get(err, 'response.data', '')
        if (!errMsg)
          errMsg = get(err, 'response.status', '')
        dispatch({ type: actions.REQUEST_END, payload: errMsg, error: true, requestId })
        return
      }
      dispatch({ type: actions.REQUEST_END, payload: '', error: false, requestId })
    } finally {
      // Cleanup: remove all tokens for this canonicalUrl
      if (cancelSourcesRef.current[canonicalUrl]) {
        delete cancelSourcesRef.current[canonicalUrl]
      }
    }
  }

  useEffect(() => {
    if (!options || !options.manual) {
      const mergedConfig = withCancelToken(config)
      executeRequest(
        mergedConfig,
        dispatch,
        options.full ? true : false,
        mergedConfig.__canonicalUrl,
        mergedConfig.__requestId
      ).catch(() => {})
    }
    return () => {
      const canonicalUrl = getCanonicalUrl(config.url)
      cancelOutstandingRequest(canonicalUrl)
    }
  }, [options, cancelOutstandingRequest, config, withCancelToken])

  const refetch = useCallback(
    (configOverride) => {
      configOverride = configToObject(configOverride)
      const mergedConfig = withCancelToken({
        ...config,
        ...(isReactEvent(configOverride) ? null : configOverride),
      })
      return executeRequest(
        mergedConfig,
        dispatch,
        options.full ? true : false,
        mergedConfig.__canonicalUrl,
        mergedConfig.__requestId
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, withCancelToken],
  )

  return [state, refetch, cancelOutstandingRequest]
}

export default useHttps
